import { AggregationDefinitionFieldArray } from '@monorepo/shared/componentsV2/aggregation/AggregationDefinitionsFieldArray/AggregationDefinitionsFieldArray';
import { AggregationColumn, ColumnType } from 'mapistry-shared';
import React, { useCallback } from 'react';
import { DeepPartial } from 'utility-types';
import { useSingleQueryStep } from '../../../contexts/SingleQueryStepContext';
import { FormValues } from './types';

export function AggregationDefinitionList() {
  const { availableColumns, columnSets, isLastStep } = useSingleQueryStep();

  const getAllowedColumnOptions = useCallback(
    (formValues: DeepPartial<FormValues>) => {
      // It doesn't make sense to aggregate and group by the same property, and since we are grouping by all column
      // children under the hood, we exclude group-by columns and their children from selection here.
      const selectedGroupedColumnNames = formValues.groupBy || [];
      const columnNamesToExclude = selectedGroupedColumnNames.flatMap(
        (columnName) => {
          const columnSet = columnSets.find(
            (cs) => cs.parentColumn === columnName,
          );
          return [columnName, ...(columnSet?.dependentColumns || [])];
        },
      );
      return availableColumns
        .filter(
          (c) =>
            // If a user doesn't see a column value, there is no reason for them to do any aggregation on such column,
            // currently (March 2025) it's only count distinct
            !c.isSystemColumn &&
            // It doesn't make sense to aggregate and group by the same column, so removing it from selectable options.
            !columnNamesToExclude.includes(c.columnName),
        )
        .map((c) => ({
          label: c.columnLabel,
          value: c.columnName,
          isNumeric: c.columnType === ColumnType.NUMBER,
        }));
    },
    [availableColumns, columnSets],
  );

  return (
    <AggregationDefinitionFieldArray<FormValues, 'columns', AggregationColumn>
      canEdit={isLastStep}
      childFieldNameForAlias="alias"
      childFieldNameForMethod="method"
      childFieldNameForColumn="columnName"
      fieldName="columns"
      getAllowedColumnOptions={getAllowedColumnOptions}
    />
  );
}
